import {
  DropdownFilter,
  FilterArrayType,
  FilterElement,
} from 'common/components/Filters';
import React from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { ChoiceGroup, IChoiceGroupOption, Stack } from '@fluentui/react';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import { InvoiceTransactionTypes } from 'common/components/Modules/TransactionEdit/graphql/__generated__/InvoiceTransactionTypes';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import { dateConvertions, dateFormat } from 'common/utils/dateFormats';
import { choiceGroupStyles, options } from 'common/utils/listViewOptionStyle';
import { useState } from 'react';
import { globalMode } from 'utility/cache/ui';
import INVOICE_TRANSACTION_TYPES from '../../../../common/components/Modules/TransactionEdit/graphql/InvoiceTransactionTypes.graphql';
import { useStyles } from './index.styles';

interface TransactionSearchBarProps extends SearchBarProps {
  disabled?: boolean;
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
  isSigningSearch: boolean;
  isHistorySearch: boolean;
  setSigningSearch?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsHistorySearch: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAccountingEntry?:
    | React.Dispatch<React.SetStateAction<boolean>>
    | undefined;
}
export const TransactionSearchBar: React.FC<TransactionSearchBarProps> = ({
  columns,
  onFilterChange,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  isSigningSearch,
  urlParams,
  onGlobalToggle,
  disabled,
  isHistorySearch,
  setSigningSearch,
  setIsHistorySearch,
  setIsAccountingEntry,
}) => {
  const globalState = useReactiveVar(globalMode);
  const styles = useStyles();
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const { data: invoiceTransactionTypesData } =
    useQuery<InvoiceTransactionTypes>(INVOICE_TRANSACTION_TYPES);
  const typeOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.invoiceTransactionTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.transactionType!,
      includeAll: true,
    }
  );
  const businessUnitOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.companyBusinessUnits?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );
  const departmentOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.companyDepartments?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );
  const statusOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.invoiceStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
    }
  );

  const companiesOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.companies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );

  const currenciesOptions = formatDropdownOptions(
    invoiceTransactionTypesData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );

  const accountingPeriodOptions =
    invoiceTransactionTypesData?.companyCorporatePeriods?.nodes.map((item) => ({
      key: item.id,
      text:
        item._periodYear +
          ' (' +
          dateFormat(dateConvertions(item.startDate)) +
          ' - ' +
          dateFormat(dateConvertions(item.endDate)) +
          ')' || '',
    })) || [];

  const invoiceUsers = formatDropdownOptions(
    invoiceTransactionTypesData?.invoiceUsers?.nodes,
    {
      getKey: (item) => item.userId!,
      getText: (item) => item.userName!,
      includeAll: true,
    }
  );
  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    let newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    newFilters = newFilters.filter((element) => element.value);
    onFilterChange(newFilters);
    setFilterTypes(newFilters);
  };
  const selectedKey = isHistorySearch
    ? 'History'
    : isSigningSearch
      ? 'Signing'
      : 'Accounting';
  const onChoiceChange = (
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    option?: IChoiceGroupOption
  ) => {
    switch (option?.key) {
      case 'Signing':
        setSigningSearch?.(true);
        setIsHistorySearch(false);
        setIsAccountingEntry?.(false);
        break;

      case 'Accounting':
        setIsAccountingEntry?.(true);
        setIsHistorySearch(false);
        setSigningSearch?.(false);
        break;

      case 'History':
        setIsHistorySearch(true);
        setSigningSearch?.(false);
        break;
    }
  };

  const filters: FilterElement[] = [
    <DropdownFilter
      filterKey="transactionTypeId"
      options={typeOptions}
      placeholder="Type"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="businessUnitId"
      options={businessUnitOptions}
      placeholder="Business Unit"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="departmentId"
      options={departmentOptions}
      placeholder="Department"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="currencyId"
      options={currenciesOptions}
      placeholder="Currency"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="_companyId"
      options={companiesOptions || []}
      placeholder="Productions"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="corporatePeriodId"
      options={accountingPeriodOptions}
      placeholder="Accounting Period"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="_createdByUserid"
      options={invoiceUsers}
      placeholder="Created by"
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="filterMultiStatusTypes"
      options={statusOptions}
      placeholder="Status"
      onOptionChange={onOptionChange}
      maxWidth={150}
      multiSelect
    />,
  ];

  return (
    <SearchBar
      disabled={disabled}
      onFilterClear={() => {
        if (filterTypes.length) onFilterChange([]);
        setFilterTypes([]);
      }}
      urlParams={urlParams}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      onGlobalToggle={onGlobalToggle}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontalAlign="center"
          verticalAlign="start"
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <ChoiceGroup
            options={options}
            disabled={globalState || disabled}
            selectedKey={selectedKey}
            styles={choiceGroupStyles}
            onChange={onChoiceChange}
          />
        </Stack>
      }
      filters={filters}
    />
  );
};
